import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext.jsx';
import api from '../api.js';

const PremiumContext = createContext(null);

export function PremiumProvider({ children }) {
  const { user } = useAuth();
  const [premium, setPremium] = useState(false);
  const [unlimited, setUnlimited] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!user?.uid) {
      setPremium(false);
      setUnlimited(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.getUserPurchases(user.uid);
      const purchases = Array.isArray(res) ? res : res?.purchases || [];

      // Unlimited implies premium too — covers manual db inserts that
      // only have one row.
      const hasPremium = purchases.some(
        (p) =>
          p.product_id === 'premium' ||
          p.productId === 'premium' ||
          p.product_id === 'unlimited' ||
          p.productId === 'unlimited'
      );
      const hasUnlimited = purchases.some(
        (p) => p.product_id === 'unlimited' || p.productId === 'unlimited'
      );

      setPremium(hasPremium);
      setUnlimited(hasUnlimited);

      // Visible diagnostic — shows up in DevTools so you can confirm the
      // UID being checked matches what's in the database.
      // eslint-disable-next-line no-console
      console.info(
        '[premium] uid=%s purchases=%o → premium=%s unlimited=%s',
        user.uid,
        purchases,
        hasPremium,
        hasUnlimited
      );
    } catch (err) {
      console.warn('[premium] status load failed for uid=%s', user.uid, err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  return (
    <PremiumContext.Provider
      value={{ premium, unlimited, loading, refreshStatus }}
    >
      {children}
    </PremiumContext.Provider>
  );
}

export function usePremium() {
  const ctx = useContext(PremiumContext);
  if (!ctx) throw new Error('usePremium must be used inside PremiumProvider');
  return ctx;
}
